import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useLanguage } from "../../context/Language/LanguageContext";
import { storeSession } from "../../context/VBLiveAPI/VBLiveAPIAction";
import { useAuthStatus } from "../hooks/useAuthStatus";
import {
    detectCatapultTeamSide,
    enrichMatchWithCatapultData,
    groupJumpsByAthlete,
    parseCatapultFocusCSV,
    playerForShirtNumber,
} from "../utils/CatapultFocusFile";
import { formatJumpHeightM, getJumpHeightUnit, jumpHeightInUnit } from "../utils/Constants";
import { makePlaylist } from "../utils/Utils";
import CatapultJumpStatsReport from "./CatapultJumpStatsReport";

function formatTime(sec) {
  if (sec == null || !Number.isFinite(sec)) return "";
  const d = new Date(sec * 1000);
  return d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function JumpChart({ jumps }) {
  const unit = getJumpHeightUnit();
  const unitLabel = unit === "in" ? "in" : "cm";
  const data = jumps.map((j) => ({
    t: j.eventSec,
    height: j.heightM != null ? jumpHeightInUnit(j.heightM * 100, unit) : 0,
  }));
  if (data.length === 0) return null;
  const maxH = Math.max(unit === "in" ? 4 : 10, ...data.map((d) => d.height));
  const step = unit === "in" ? 2 : 5;
  const yMaxNice = Math.ceil(maxH / step) * step;
  return (
    <ResponsiveContainer width="100%" height={120}>
      <BarChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="t"
          type="number"
          scale="time"
          domain={["dataMin", "dataMax"]}
          tickFormatter={formatTime}
          tick={{ fontSize: 9 }}
        />
        <YAxis domain={[0, yMaxNice]} tick={{ fontSize: 9 }} />
        <Tooltip
          formatter={(v) => [`${Number(v).toFixed(1)} ${unitLabel}`, "Height"]}
          labelFormatter={(v) => formatTime(Number(v))}
        />
        <Bar dataKey="height" fill="#10b981" isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// Jump data report for a single match: lets the coach pick a Catapult
// "Focus" CSV export (shirt-number-tagged jumps + period markers) and shows
// each player's jumps, synchronising the sensor clock with the match's own
// wall-clock via the same offset-histogram technique the sibling
// catapult-vb project uses against the live Catapult API.
function CatapultReport({ match }) {
  const { t } = useLanguage();
  const { currentUser } = useAuthStatus();
  const navigate = useNavigate();
  const [fileName, setFileName] = useState(null);
  const [parsed, setParsed] = useState(null); // { periods, jumps }
  const [error, setError] = useState(null);
  const fileRef = useRef();
  // Jump Report modal -- the group (shirt/lastName/jumps) currently being
  // shown, or null when closed.
  const [reportGroup, setReportGroup] = useState(null);
  // Players whose Catapult jumps couldn't be tied to anyone with recorded
  // match stats (unrostered/unmatched sensor wearers) clutter the grid --
  // hidden by default, toggle to show them.
  const [showNoStatsPlayers, setShowNoStatsPlayers] = useState(false);
  // Which team ("A"/"B") this Focus file's players belong to -- a Focus
  // export only ever covers one team, and shirt numbers collide across
  // teams, so everything downstream needs this to match the right player.
  const [teamSide, setTeamSide] = useState(null);
  const [askTeamSide, setAskTeamSide] = useState(false);

  // Resolve which team this Focus file belongs to: reuse a previously-saved
  // choice if there is one, else auto-detect, else ask the user.
  useEffect(() => {
    if (!parsed || !match) {
      setTeamSide(null);
      setAskTeamSide(false);
      return;
    }
    if (parsed.teamSide === "A" || parsed.teamSide === "B") {
      setTeamSide(parsed.teamSide);
      setAskTeamSide(false);
      return;
    }
    const detection = detectCatapultTeamSide(match, parsed.jumps);
    if (detection.confident) {
      parsed.teamSide = detection.side;
      setTeamSide(detection.side);
      setAskTeamSide(false);
    } else {
      setTeamSide(null);
      setAskTeamSide(true);
    }
  }, [parsed, match]);

  // Saves the user's manual team pick (when auto-detection was ambiguous) so
  // it's remembered next time this match's Catapult data is opened.
  const chooseTeamSide = async (side) => {
    parsed.teamSide = side;
    setTeamSide(side);
    setAskTeamSide(false);
    match.catapultFocusData = parsed;
    if (match?.guid && currentUser?.email) {
      try {
        await storeSession(match, currentUser);
      } catch (saveErr) {
        console.error(saveErr);
      }
    }
  };

  // Restore previously-saved jump data for this match (decompressed by
  // fsFetchSessionById onto match.catapultFocusData) so it's ready to use
  // without re-uploading the CSV every time the match is opened.
  useEffect(() => {
    if (!parsed && match?.catapultFocusData) {
      setParsed(match.catapultFocusData);
      setFileName(t("Loaded from saved match data"));
    }
  }, [match, parsed, t]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      const text = await file.text();
      const result = parseCatapultFocusCSV(text);
      if (result.jumps.length === 0) {
        setError(t("No jump data found in this file."));
        setParsed(null);
        setFileName(null);
        return;
      }
      setParsed(result);
      setFileName(file.name);

      // Compress and save alongside the match's own data so it's restored
      // automatically next time this match is opened.
      match.catapultFocusData = result;
      if (match?.guid && currentUser?.email) {
        try {
          const ret = await storeSession(match, currentUser);
          if (ret === 0) {
            toast.error(t("Could not save jump data with this match."));
          } else {
            toast.success(t("Jump data saved with this match."));
          }
        } catch (saveErr) {
          console.error(saveErr);
          toast.error(t("Could not save jump data with this match."));
        }
      }
    } catch (err) {
      console.error(err);
      setError(
        t(
          "Could not read this file. Please check it is a Catapult Focus CSV export."
        )
      );
      setParsed(null);
    } finally {
      e.target.value = null;
    }
  };

  const groups = useMemo(
    () => (parsed ? groupJumpsByAthlete(parsed.jumps) : []),
    [parsed]
  );

  // Gathers this player's synced jump events (serve/set/spike/block/setter's
  // call with a matched Catapult height) into a playlist and opens it, so
  // the coach can watch every one of their jumps back to back.
  const doWatchVideo = (g) => {
    const evs = (match?.events ?? []).filter(
      (ev) =>
        Number(ev.Player?.shirtNumber ?? ev.Player?.ShirtNumber) === g.shirtNumber &&
        ev.imaHeightM != null
    );
    if (evs.length === 0) {
      toast.error(t("No synced jump events found for this player."));
      return;
    }
    // Same guard VideoAnalysis.jsx's own createPlaylist uses -- a playlist
    // item with no videoOnlineUrl has nothing to play, so catch it here
    // with a clear message instead of opening a silently video-less playlist.
    if (!match.videoOnlineUrl) {
      toast.error(t("This match has no video attached yet."));
      return;
    }
    const sortedEvs = evs.sort((a, b) => a.TimeStamp.getTime() - b.TimeStamp.getTime());
    const playlist = makePlaylist(sortedEvs);
    localStorage.setItem("VBLivePlaylistEvents", JSON.stringify(playlist));
    const player = playerForShirtNumber(match, g.shirtNumber);
    const playerLabel = player ? player.NickName || player.LastName : g.lastName;
    const st = {
      playlistFileData: JSON.stringify({ events: playlist }),
      filename: null,
      playlists: null,
      serverName: currentUser?.email,
      description: `${playerLabel} - jumps`,
      initialTags: [],
    };
    navigate("/playlist", { state: st });
  };

  // Shirt numbers with at least one recorded match event (any skill) --
  // "has match stats", regardless of whether that player ever jumped. Scoped
  // to the established team so a same-numbered player on the OTHER team
  // doesn't make this one look like it "has stats" too.
  const hasStatsShirts = useMemo(() => {
    const shirts = new Set();
    for (const ev of match?.events ?? []) {
      if (teamSide) {
        const roster = teamSide === "A" ? match.teamA?.players : match.teamB?.players;
        if (!(roster ?? []).some((p) => p === ev.Player || p.Guid === ev.Player?.Guid)) continue;
      }
      const shirt = Number(ev.Player?.shirtNumber ?? ev.Player?.ShirtNumber);
      if (!isNaN(shirt)) shirts.add(shirt);
    }
    return shirts;
  }, [match, teamSide]);

  const visibleGroups = useMemo(
    () => groups.filter((g) => showNoStatsPlayers || hasStatsShirts.has(g.shirtNumber)),
    [groups, hasStatsShirts, showNoStatsPlayers]
  );
  const hiddenCount = groups.length - visibleGroups.length;

  // Synchronises the sensor clock with the match's own wall-clock and
  // attaches imaHeightM/imaDurationSec onto each matched event + each
  // player's own jumps onto their player object (all mutated in place on
  // `match`, shared with Video Analysis) -- same function Session.jsx calls
  // as soon as a match with saved Catapult data loads, so this just
  // refreshes it when a fresh CSV is parsed here.
  const [sync, setSync] = useState(null);
  const [enrichedCount, setEnrichedCount] = useState(0);
  useEffect(() => {
    if (!match || !parsed || !teamSide) {
      setSync(null);
      setEnrichedCount(0);
      return;
    }
    const result = enrichMatchWithCatapultData(match, teamSide);
    setSync({ perPlayer: result.perPlayer, overall: result.overall });
    setEnrichedCount(result.enrichedCount);
  }, [match, parsed, teamSide]);

  if (!match) {
    return (
      <p className="p-4 text-sm text-base-content/60">
        {t("No match loaded.")}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-2">
      <div className="flex items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={handleFile}
        />
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => fileRef.current?.click()}
        >
          {t("Select Catapult Focus file...")}
        </button>
        {fileName && (
          <span className="text-xs text-base-content/60">{fileName}</span>
        )}
      </div>

      {error && (
        <div className="alert alert-error text-xs p-2">
          <span>{error}</span>
        </div>
      )}

      {!parsed ? (
        <p className="text-sm text-base-content/60">
          {t(
            "Select a Catapult Focus CSV export to view jump data for this match."
          )}
        </p>
      ) : (
        <>
          <div className="text-xs text-base-content/60">
            {parsed.jumps.length} {t("jumps")} · {groups.length}{" "}
            {t("players")} · {parsed.periods.length} {t("periods")}
            {teamSide && (
              <>
                {" "}
                · {t("Team")}:{" "}
                {teamSide === "A" ? match.teamA?.Name : match.teamB?.Name}
              </>
            )}
            {sync?.overall && (
              <>
                {" "}
                · {t("Sync offset")}: {sync.overall.offset.toFixed(1)}s (
                {sync.overall.matchCount}/{sync.overall.matchTotal}{" "}
                {t("matched")})
              </>
            )}
            {enrichedCount > 0 && (
              <>
                {" "}
                · {enrichedCount}{" "}
                {t(
                  "match events now show jump height/duration in Video Analysis"
                )}
              </>
            )}
          </div>

          <label className="flex items-center gap-2 text-xs cursor-pointer w-fit">
            <input
              type="checkbox"
              className="checkbox checkbox-xs"
              checked={showNoStatsPlayers}
              onChange={(e) => setShowNoStatsPlayers(e.target.checked)}
            />
            {t("Show players with no match stats")}
            {hiddenCount > 0 && !showNoStatsPlayers && (
              <span className="text-base-content/50">
                ({hiddenCount} {t("hidden")})
              </span>
            )}
          </label>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {visibleGroups.map((g) => {
              const player = playerForShirtNumber(match, g.shirtNumber, teamSide);
              const heights = g.jumps
                .map((j) => j.heightM)
                .filter((h) => h != null);
              const maxH = heights.length ? Math.max(...heights) : null;
              const avgH = heights.length
                ? heights.reduce((a, b) => a + b, 0) / heights.length
                : null;
              const playerSync =
                g.shirtNumber != null ? sync?.perPlayer?.[g.shirtNumber] : null;
              return (
                <div
                  key={`${g.shirtNumber}-${g.lastName}`}
                  className="card bg-base-200 shadow-sm"
                >
                  <div className="card-body gap-1 p-3">
                    <h4 className="card-title text-sm">
                      {g.shirtNumber != null ? `#${g.shirtNumber} ` : ""}
                      {player ? player.NickName || player.LastName : g.lastName}
                    </h4>
                    <div className="text-xs text-base-content/60">
                      {g.jumps.length} {t("jumps")}
                      {maxH != null && (
                        <>
                          {" "}
                          · {t("max")} {formatJumpHeightM(maxH)}
                        </>
                      )}
                      {avgH != null && (
                        <>
                          {" "}
                          · {t("avg")} {formatJumpHeightM(avgH)}
                        </>
                      )}
                    </div>
                    {playerSync && (
                      <div className="text-[10px] text-base-content/50">
                        {t("Sync")}: {playerSync.matchCount}/
                        {playerSync.matchTotal} {t("matched")}
                      </div>
                    )}
                    <JumpChart jumps={g.jumps} />
                    <div className="flex gap-1">
                      <button
                        type="button"
                        className="btn btn-xs btn-outline mt-1"
                        onClick={() => setReportGroup(g)}
                      >
                        {t("Jump Report")}
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs btn-outline mt-1"
                        onClick={() => doWatchVideo(g)}
                      >
                        {t("Watch Video")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {reportGroup && (
        <CatapultJumpStatsReport
          match={match}
          player={playerForShirtNumber(match, reportGroup.shirtNumber, teamSide)}
          shirtNumber={reportGroup.shirtNumber}
          lastName={reportGroup.lastName}
          jumps={reportGroup.jumps}
          offset={sync?.perPlayer?.[reportGroup.shirtNumber]?.offset ?? sync?.overall?.offset}
          onClose={() => setReportGroup(null)}
        />
      )}
      {askTeamSide && (
        <div className="modal modal-open z-[9999]">
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg mb-2">
              {t("Which team is this Catapult file for?")}
            </h3>
            <p className="text-sm text-base-content/70 mb-4">
              {t(
                "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to."
              )}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => chooseTeamSide("A")}
              >
                {match.teamA?.Name ?? t("Team A")}
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => chooseTeamSide("B")}
              >
                {match.teamB?.Name ?? t("Team B")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CatapultReport;
